import type { HardwareWalletSigningState } from '@/hooks/useHardwareWalletSigning';
import { FullscreenContainer } from '@infinex-private/connect-web-shared/components';
import type { ExternalSigningDeviceType } from '@infinex/types';
import { ChevronLeftIcon } from '@infinex/ui-icons';
import { Button, Text } from '@infinex/ui/components';
import {
  ledgerConnectingLottie,
  trezorConnectingLottie,
} from '@infinex/web-shared/components/hardware-wallet';
import { Lottie } from '@infinex/web-shared/core/lottie';

interface HardwareWalletSigningViewProps {
  deviceType: ExternalSigningDeviceType;
  state: HardwareWalletSigningState;
  error: Error | null;
  onCancel: () => void;
  onRetry: () => void;
  onBack?: () => void;
}

const STATE_MESSAGES: Record<HardwareWalletSigningState, string> = {
  idle: '',
  connecting: 'Connecting to your hardware wallet...',
  awaiting_confirmation: 'Confirm this transaction on your hardware wallet',
  signing: 'Signing transaction...',
  broadcasting: 'Broadcasting transaction...',
  success: 'Transaction signed successfully',
  error: 'Something went wrong',
};

export function HardwareWalletSigningView({
  deviceType,
  state,
  error,
  onCancel,
  onRetry,
  onBack,
}: HardwareWalletSigningViewProps) {
  const isLedger = deviceType === 'ledger';
  const animationData = isLedger
    ? ledgerConnectingLottie
    : trezorConnectingLottie;

  const message = error?.message ?? STATE_MESSAGES[state];
  const showRetry = state === 'error' || state === 'connecting';

  return (
    <FullscreenContainer className="bg-canvas size-full rounded-none">
      <div className="flex h-full flex-col">
        {/* Header with back button */}
        {onBack && (
          <div className="px-4 py-3">
            <button
              type="button"
              onClick={onBack}
              className="text-default flex items-center gap-1 text-sm"
            >
              <ChevronLeftIcon size="small" />
            </button>
          </div>
        )}

        {/* Animation and message content */}
        <div className="flex flex-1 flex-col items-center justify-center px-8">
          <div className="flex flex-col items-center gap-6">
            <Lottie
              loop
              autoplay
              animationData={animationData}
              className="h-auto w-full max-w-xs"
            />

            <Text className="text-secondary text-center text-sm">
              {message}
            </Text>

            {error && (
              <div className="bg-fillCriticalEmphasis/10 w-full rounded-lg p-3">
                <Text className="text-critical text-center text-sm">
                  {error.message}
                </Text>
              </div>
            )}
          </div>
        </div>

        {/* Footer with Cancel/Retry buttons */}
        <footer className="mt-auto border-t px-8 py-4">
          <div className="grid grid-cols-2 gap-3">
            <Button prominence="default" size="large" onPress={onCancel}>
              Cancel
            </Button>
            <Button
              prominence={showRetry ? 'high' : 'default'}
              size="large"
              onPress={onRetry}
            >
              Retry
            </Button>
          </div>
        </footer>
      </div>
    </FullscreenContainer>
  );
}
